import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Box,
  Typography,
} from "@mui/material";

interface Row {
  id: number;
  [key: string]: any;
}

interface Column {
  field: string;
  headerName: string | React.ReactNode | ((rows: Row[]) => React.ReactNode);
  renderCell?: (params: { value: any; row: Row }) => React.ReactNode;
  align?: "inherit" | "left" | "center" | "right" | "justify";
}

interface KenTableProps {
  columns: any[];
  rows: Row[];
  showCheckbox?: boolean;
  border?: boolean;
  footer?: React.ReactNode;
  onSelectionChange?: (selectedIds: number[]) => void;
}

const KenTable: React.FC<KenTableProps> = ({
  columns,
  rows,
  showCheckbox,
  footer,
  border,
  onSelectionChange,
}) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newSelected: number[] = [];
    if (event.target.checked) {
      newSelected = rows
        .filter((r) => r.eligibility !== "Not eligible")
        .map((r) => r.id);
    }
    setSelectedRows(newSelected);
    onSelectionChange?.(newSelected);
  };

  const handleCheckboxToggle = (id: number) => {
    setSelectedRows((prev) => {
      const newSelected = prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id];
      onSelectionChange?.(newSelected);
      return newSelected;
    });
  };

  const allSelected =
    rows.length > 0 &&
    selectedRows.length ===
      rows.filter((r) => r.eligibility !== "Not eligible").length;

  return (
    <Box
      sx={{
        borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "white",
        border: border ? "0.3px solid #B2B3B7 !important" : "none",
      }}
    >
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
            <TableRow>
              {showCheckbox && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length <
                        rows.filter(
                          (r) => r.eligibility !== "Not eligible"
                        ).length
                    }
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.align || "left"}
                  sx={{ fontWeight: 600, textAlign: col.align || "left" }}
                >
                  {typeof col.headerName === "function"
                    ? col.headerName(rows)
                    : col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const isSelected = selectedRows.includes(row.id);
              return (
                <TableRow key={row.id} hover>
                  {showCheckbox && (
                    <TableCell padding="checkbox" sx={{ border: "none" }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleCheckboxToggle(row.id)}
                        disabled={row.eligibility === "Not eligible"}
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => {
                    const value = row[col.field];
                    return (
                      <TableCell
                        key={col.field}
                        align={col.align || "left"}
                        sx={{ border: "none", textAlign: col.align || "left" }}
                      >
                        {col.renderCell
                          ? col.renderCell({ value, row })
                          : value ?? "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {footer && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: "#F9FAFB",
            borderTop: "1px solid #E5E7EB",
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
};

export default KenTable;
